import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { PageHeader } from '@/components/common/PageHeader'
import { StatusBadge } from '@/components/common/StatusBadge'
import { EmptyState } from '@/components/common/EmptyState'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type Project = Tables<'projects'>

export function ProjectsListPage() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!profile?.organization_id) return
    async function load() {
      let query = supabase.from('projects').select('*')
        .eq('organization_id', profile!.organization_id!)
        .order('created_at', { ascending: false })

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter as Tables<'projects'>['status'])
      }

      const { data } = await query
      setProjects(data || [])
      setLoading(false)
    }
    load()
  }, [profile, statusFilter])

  const filtered = projects.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase())
    || p.reference_code.toLowerCase().includes(search.toLowerCase())
    || (p.customer_name && p.customer_name.toLowerCase().includes(search.toLowerCase()))
  )

  const canCreate = profile && ['sales_consultant', 'operations', 'management', 'partner_admin', 'keder_admin'].includes(profile.role)

  return (
    <div>
      <PageHeader
        title="Projects"
        description="Manage all commercial engagements"
        actions={canCreate ? <Link to="/gaviota/projects/new" className={css.btnPrimary}>New Project</Link> : undefined}
      />

      <div style={{ display: 'flex', gap: 'var(--space-4)', marginBottom: 'var(--space-6)', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search projects..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={css.formInput}
          style={{ maxWidth: 300 }}
        />
        <div className={css.tabs} style={{ marginBottom: 0, borderBottom: 'none' }}>
          {['all', 'draft', 'active', 'on_hold', 'completed'].map(s => (
            <button key={s} className={`${css.tab} ${statusFilter === s ? css.tabActive : ''}`} onClick={() => setStatusFilter(s)}>
              {s === 'all' ? 'All' : s.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No projects found"
          description={search ? 'Try adjusting your search' : 'Create your first project to get started'}
          action={canCreate ? <Link to="/gaviota/projects/new" className={css.btnPrimary}>New Project</Link> : undefined}
        />
      ) : (
        <div className={css.card} style={{ padding: 0, overflow: 'hidden' }}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Name</th>
                <th>Customer</th>
                <th>Segment</th>
                <th>Status</th>
                <th>Value</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className={css.tableClickableRow} onClick={() => navigate(`/gaviota/projects/${p.id}`)}>
                  <td><code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{p.reference_code}</code></td>
                  <td style={{ fontWeight: 500 }}>{p.name}</td>
                  <td>{p.customer_name || '-'}</td>
                  <td><StatusBadge label={p.segment} variant="default" /></td>
                  <td><StatusBadge label={p.status} /></td>
                  <td>{p.estimated_value ? `£${Number(p.estimated_value).toLocaleString()}` : '-'}</td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>{new Date(p.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
